import React, { useState } from 'react';
import Styles from '../../assets/css/auth/newPassword.module.scss';
import { useNavigate } from 'react-router-dom';
import ForgotPasswordLogo from '../../assets/images/logo/Forgot_password.png';
import OpenEye from '../../assets/images/Auth/visibility.png';
import CloseEye from '../../assets/images/Auth/visibility_off.png';
import { adminNewPass } from '../../services/Api';
import { AiOutlineLoading } from 'react-icons/ai';
import { useDispatch } from 'react-redux';
import { popUpBoxTrigger } from "../../features/message/popUpSlice";
import PopUpMessage from "../../utils/errors/PopUpMessage";
import { responseHandler } from '../../utils/responseHandler';
import { validatePassword } from '../../utils/validationHandler';

const NewPassword = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [newPasswordVisible, setNewPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});

    // Default email
    const email = "admin@aits.com";

    const showPopup = (type, heading, message) => {
        dispatch(popUpBoxTrigger({
            type,
            heading,
            message,
            show: true
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // In src/pages/authentication/NewPassword.jsx
// Only the modified handleNewPassword function:

const handleNewPassword = async (e) => {
    e.preventDefault();

    // Validate passwords
    const { isValid, errors: validationErrors } = validatePassword(
        formData.password,
        formData.confirmPassword
    );

    if (!isValid) {
        setErrors(validationErrors);
        return;
    }

    setLoading(true);
    try {
        const newPass = {
            email: email,
            password: formData.password,
            password_confirmation: formData.confirmPassword
        };

        const response = await adminNewPass(newPass);
        const result = await responseHandler(response);

        if (result.status === 200) {
            showPopup('success', 'Password Updated', 
                'Your password has been successfully updated.');
            
            await new Promise(resolve => setTimeout(resolve, 1000));
            navigate("/login");
        } else {
            // Handle the password leak error
            if (result.errors && result.errors.password) {
                setErrors({
                    password: result.errors.password[0],
                    confirmPassword: ''
                });
                showPopup('error', 'Security Alert', 
                    'The password you entered has been compromised in a data leak. Please choose a different password.');
                
                // Clear password fields
                setFormData(prev => ({
                    ...prev,
                    password: '',
                    confirmPassword: ''
                }));
            } else {
                showPopup('error', 'Update Failed', 
                    result.message || 'Failed to update password.');
            }
        }
    } catch (error) {

        if (!navigator.onLine) {
            showPopup('error', 'Connection Error', 
                'Please check your internet connection.');
        } else if (error.response) {
            // Handle the password leak error from error response
            if (error.response.data && 
                error.response.data.errors && 
                error.response.data.errors.password) {
                setErrors({
                    password: error.response.data.errors.password[0],
                    confirmPassword: ''
                });
                showPopup('error', 'Security Alert', 
                    'The password you entered has been compromised in a data leak. Please choose a different password.');
                
                // Clear password fields
                setFormData(prev => ({
                    ...prev,
                    password: '',
                    confirmPassword: ''
                }));
            } else {
                const status = error.response.status;
                switch (status) {
                    case 400:
                        showPopup('error', 'Invalid Request', 
                            'Please check your password requirements.');
                        break;
                    case 401:
                        showPopup('error', 'Session Expired', 
                            'Your session has expired. Please start over.');
                        break;
                    default:
                        showPopup('error', 'Error', 
                            error.response.data.message || 'Failed to update password.');
                }
            }
        } else {
            showPopup('error', 'Error', 'Unable to connect to the server.');
        }
    } finally {
        setLoading(false);
    }
};

    const toggleNewPasswordVisibility = () => {
        setNewPasswordVisible(!newPasswordVisible);
    };

    const toggleConfirmPasswordVisibility = () => {
        setConfirmPasswordVisible(!confirmPasswordVisible);
    };

    return (
        <div className={`${Styles.container}`}>
            <PopUpMessage />
            
            <div className={`${Styles.newBox}`}>
                <img src={ForgotPasswordLogo} alt="forgot password" />
                <h2 className={`${Styles.title}`}>New Password</h2>
                {/* <p className={`${Styles.subtitle}`}>
                    Your password must be at least 8 characters long with 1 uppercase letter,
                    1 number, and 1 special character.
                </p> */}
                <p className={`${Styles.subtitle}`}>Your password should be unique and not easily accessible.</p>

                <div className={`${Styles.form}`}>
                    <div className={`${Styles.inputGroup} ${errors.password ? Styles.error : ''}`}>
                        <label>New Password</label>
                        <div className={`${Styles.passwordContainer}`}>
                            <input
                                name="password"
                                type={newPasswordVisible ? "text" : "password"}
                                placeholder="Enter New Password"
                                value={formData.password}
                                onChange={handleInputChange}
                            />
                            <img
                                src={newPasswordVisible ? OpenEye : CloseEye}
                                alt="Toggle Password Visibility"
                                onClick={toggleNewPasswordVisibility}
                                className={`${Styles.eyeIcon}`}
                            />
                        </div>
                        {errors.password && 
                            <span className={Styles.errorMessage}>{errors.password}</span>
                        }
                    </div>

                    <div className={`${Styles.inputGroup} ${errors.confirmPassword ? Styles.error : ''}`}>
                        <label>Confirm Password</label>
                        <div className={`${Styles.passwordContainer}`}>
                            <input
                                name="confirmPassword"
                                type={confirmPasswordVisible ? "text" : "password"}
                                placeholder="Confirm New Password"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                            />
                            <img
                                src={confirmPasswordVisible ? OpenEye : CloseEye}
                                alt="Toggle Password Visibility"
                                onClick={toggleConfirmPasswordVisibility}
                                className={`${Styles.eyeIcon}`}
                            />
                        </div>
                        {errors.confirmPassword && 
                            <span className={Styles.errorMessage}>{errors.confirmPassword}</span>
                        }
                    </div>

                    <button onClick={handleNewPassword} className={`${Styles.newPasswordButton}`} disabled={loading}>
                        {loading ? <AiOutlineLoading /> : "Continue"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default NewPassword;